import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { sheetsClient } from '../data/sheetsClient';

/**
 * Every entity here is fetched once when the teacher logs in (TeacherApp
 * mount) and shared across pages, instead of each page independently
 * re-fetching the same data every time it mounts. Writes that change one of
 * these should call refetch.<key>() rather than reloading everything.
 */
const ENTITY_FETCHERS = {
  school: () => sheetsClient.getCurrentSchool(),
  event: () => sheetsClient.getEventDetails(),
  timeline: () => sheetsClient.getTimeline(),
  entries: () => sheetsClient.getEntries(),
  dancers: () => sheetsClient.getDancers(),
  groups: () => sheetsClient.getGroups(),
  choreographerLink: () => sheetsClient.getChoreographerLink(),
  staff: () => sheetsClient.getStaff(),
  locks: () => sheetsClient.getSchoolLocks(),
  entryFeePayments: () => sheetsClient.getEntryFeePayments(),
  feeConfig: () => sheetsClient.getFeeConfig(),
  invoiceSummary: () => sheetsClient.getInvoiceSummary(),
};

const ENTITY_KEYS = Object.keys(ENTITY_FETCHERS);

// Data older than this is quietly re-fetched when its page is opened or the
// browser tab comes back into view (see refreshIfStale). Teachers can still
// reload the page for anything they're waiting on right now.
const STALE_AFTER_MS = 15 * 60 * 1000;

// Every page consuming these assumes its shape (e.g. StaffChaperones.js
// reads staff.length) once loading.<key> goes false — which used to also
// happen after a FAILED fetch, since load() below only ever set data[key]
// on success, leaving it undefined otherwise. That undefined then crashed
// the page (blank screen) on nothing worse than a transient 500, instead of
// letting the page render its own empty state. Seeding these up front means
// a failed fetch degrades to "shows nothing" rather than "throws".
const ENTITY_DEFAULTS = {
  school: null,
  event: [],
  timeline: [],
  entries: [],
  dancers: [],
  groups: [],
  choreographerLink: null,
  staff: [],
  locks: null,
  entryFeePayments: [],
  feeConfig: null,
  invoiceSummary: null,
};

const TeacherDataContext = createContext(null);

export function TeacherDataProvider({ children }) {
  const [data, setData] = useState(() => ({ ...ENTITY_DEFAULTS }));
  const [loading, setLoading] = useState(() =>
    ENTITY_KEYS.reduce((acc, key) => { acc[key] = true; return acc; }, {})
  );
  const [error, setError] = useState({});

  // When each entity last loaded successfully, and which are loading right
  // now — for refreshIfStale below. Everything starts as in flight: the
  // initial load below runs after the pages' own effects (React runs child
  // effects first), so a page's first refreshIfStale must not double it.
  const loadedAt = useRef({});
  const inFlight = useRef(new Set(ENTITY_KEYS));

  // quiet: a background refresh — leaves loading.<key> alone so the page
  // keeps showing its current data (no skeleton flash) until the new data
  // arrives, and keeps the old data if the refresh fails.
  const load = useCallback(async (key, { quiet = false } = {}) => {
    inFlight.current.add(key);
    if (!quiet) {
      setLoading((prev) => ({ ...prev, [key]: true }));
      setError((prev) => ({ ...prev, [key]: null }));
    }
    try {
      const result = await ENTITY_FETCHERS[key]();
      setData((prev) => ({ ...prev, [key]: result }));
      if (quiet) setError((prev) => (prev[key] ? { ...prev, [key]: null } : prev)); // recovered
      loadedAt.current[key] = Date.now();
    } catch (err) {
      if (!quiet) setError((prev) => ({ ...prev, [key]: err }));
    } finally {
      inFlight.current.delete(key);
      if (!quiet) setLoading((prev) => ({ ...prev, [key]: false }));
    }
  }, []);

  // Quietly re-fetches whichever of `keys` were last loaded more than
  // STALE_AFTER_MS ago (and aren't already loading). Called when the teacher
  // opens a page or comes back to the browser tab, so data changed by
  // someone else (a parent's consent, a payment OSSDF recorded) shows up
  // without a manual refresh — but nothing is re-read while it's still fresh.
  const refreshIfStale = useCallback((keys) => {
    const now = Date.now();
    keys.forEach((key) => {
      if (!ENTITY_FETCHERS[key] || inFlight.current.has(key)) return;
      if (now - (loadedAt.current[key] || 0) > STALE_AFTER_MS) load(key, { quiet: true });
    });
  }, [load]);

  useEffect(() => {
    ENTITY_KEYS.forEach((key) => { load(key); });
  }, [load]);

  // load() is referentially stable, so this only needs to be built once.
  const refetch = useMemo(() => {
    const obj = {};
    ENTITY_KEYS.forEach((key) => { obj[key] = () => load(key); });
    return obj;
  }, [load]);

  // For a write whose exact result the caller already knows (e.g. a single
  // field just saved by its own value), patching local state directly is
  // both faster and avoids the visible flash refetch.<key>() causes by
  // toggling loading.<key> back to true — every page keyed off that flag
  // (StaffChaperones.js included) drops back to its loading skeleton for
  // the round trip. Reserve refetch.<key>() for when the true post-write
  // shape isn't known locally (added/removed rows, server-computed fields).
  const patch = useMemo(() => {
    const obj = {};
    ENTITY_KEYS.forEach((key) => { obj[key] = (updater) => setData((prev) => ({ ...prev, [key]: updater(prev[key]) })); });
    return obj;
  }, []);

  const value = useMemo(() => ({ ...data, loading, error, refetch, patch, refreshIfStale }), [data, loading, error, refetch, patch, refreshIfStale]);

  return <TeacherDataContext.Provider value={value}>{children}</TeacherDataContext.Provider>;
}

export function useTeacherData() {
  const ctx = useContext(TeacherDataContext);
  if (!ctx) throw new Error('useTeacherData must be used within a TeacherDataProvider');
  return ctx;
}
